import React from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { RootState } from 'store/store';
import { deleteRequest, getRequest, postRequest } from 'api/apiClient';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { User } from 'types/userTypes';
import { Role } from 'types/roleTypes';
import { capitalizeName, formatFullName, hasPermission } from 'utils/utils';
import { TABLE_HEIGHT } from 'constants/ui';
import { USER_DELETED_ID } from 'constants/userActivities';
import { useTranslation } from 'react-i18next';

interface UsersTableProps {
    users: User[];
    roles: Role[];
    loading: boolean;
    setUsers: (user: User[]) => void;
    handleEditClick: (user: User) => void;
    handleAddClick: () => void;
}

const UsersTable: React.FC<UsersTableProps> = ({ users, roles, loading, setUsers, handleEditClick, handleAddClick }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const [t] = useTranslation();
    const hasUserWrite = hasPermission(tokenScopeList, orgId, 'o', 'user:w');

    const handleDeleteUser = async (user: User) => {
        if (!!user?.masterIntegrationId) {
            notification.error({
                message: t('notification.error'),
                description: t('user.notification.deleteErrorExternalSystem'),
                placement: 'bottomRight',
            });
            return;
        }

        const superAdminRole = roles.find(role => role.admin);

        if (!superAdminRole) {
            notification.error({
                message: t('notification.error'),
                description: t('user.notification.deleteErrorSuperAdminNotFound'),
                placement: 'bottomRight',
            });
            return;
        }

        try {
            // Fetch users in "Super Admin" role
            const superAdminUsersResponse = await getRequest(`/orgs/${orgId}/roles/${superAdminRole.id}/users`);
            const superAdminUsers = superAdminUsersResponse.data.filter((user: User) => user.status === 1);

            // Check if the only remaining "Super Admin" user is being deleted
            if (superAdminUsers.length === 1 && superAdminUsers[0].id === user.id) {
                notification.error({
                    message: t('notification.error'),
                    description: t('user.notification.deleteErrorSuperAdmin', { role: superAdminRole.name || '' }),
                    placement: 'bottomRight',
                });
                return;
            }

            await deleteRequest(`/orgs/${orgId}/users/${user.id}`);
            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: USER_DELETED_ID,
                details: formatFullName(user.identity.firstName, user.identity.middleName, user.identity.lastName)
            });

            // Re-fetch the updated list of users
            const updatedUsers = await getRequest(`/orgs/${orgId}/users`);
            const filteredUsers = updatedUsers.data.filter((user: User) => user.status === 1);
            setUsers(filteredUsers);

            notification.success({
                message: t('notification.success'),
                description: t('user.notification.deleteSuccess'),
                placement: 'bottomRight',
            });
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('user.notification.deleteError'),
                placement: 'bottomRight',
            });
        }
    };

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: ['identity', 'firstName'],
            label: t('user.table.firstName'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.identity.firstName || '').localeCompare(b.identity.firstName || ''),
            render: (value: string) => capitalizeName(value),
        },
        {
            dataIndex: ['identity', 'lastName'],
            label: t('user.table.lastName'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.identity.lastName || '').localeCompare(b.identity.lastName || ''),
            render: (value: string) => capitalizeName(value),
        },
        {
            dataIndex: ['identity', 'email'],
            label: t('user.table.email'),
            filter: { type: 'input' },
            sorter: (a, b) => (a.identity.email || '').localeCompare(b.identity.email || ''),
        }
    ];

    const opTableProps: IOpTableProps = {
        dataSource: users,
        columns: columns,
        rowActions: {
            onEditClick: (user: User) => handleEditClick(user),
            onDeleteClick: hasUserWrite ? handleDeleteUser : undefined,
            deleteModalTitle: () => t('confirmDelete.title', { title: t('user.itemName') }),
            deleteModalContent: ({ record }) => {
                const fullName = formatFullName(record.identity.firstName, record.identity.middleName, record.identity.lastName);
                return t('confirmDelete.message', { name: fullName });
            },
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        writeAccess: hasUserWrite,
        allowAddition: hasUserWrite ? {
            itemName: t('user.itemName'),
            onClick: () => handleAddClick(),
        } : false,
        loading: loading,
        allowExport: true,
        allowShowHideColumns: true,
    };

    return (
        <OpTable {...opTableProps} />
    );
};

export default UsersTable;
